import React, { useCallback, useEffect, useState } from 'react';
import { addMonths, startOfDay, startOfToday } from 'date-fns';
import DatesSelector from '../DatesSelector';
import { MAX_AVAILABILITY_MONTHS_AHEAD } from '../../../../common/bookingConstants';
import { useSearchData } from '../../../../common/hooks/useSearchData';
import { usePropertyId } from '../../../../common/hooks/usePropertyId';
import { useAvailabilityAndPricingApi } from '../../../../common/hooks/useAvailabilityAndPricingApi';
import { Box, Typography } from '@mui/material';
import { OrdinaryButton } from '../../../../common/Buttons';
import RoomOffers from './RoomOffers';

/**
 * Step: dates, adults, and room/plan selection. Receives booking, onSubmit, onBack, onChange.
 */
const DatesAndAccommodationStep = ({ booking, onSubmit, onChange }) => {
  const { checkin, checkout, adults } = booking;
  const today = startOfToday();

  const propertyId = usePropertyId();
  const { getCheckinAvailability, getCheckoutAvailability } = useAvailabilityAndPricingApi();

  /** Full allowed check-in window: today through booking horizon (matches API cap). */
  const getAvailableCheckinDates = useCallback(async () => {
    if (!propertyId) return {};
    const from = startOfDay(startOfToday());
    const to = startOfDay(addMonths(startOfToday(), MAX_AVAILABILITY_MONTHS_AHEAD));
    return getCheckinAvailability(propertyId, from, to);
  }, [propertyId, getCheckinAvailability]);

  const getAvailableCheckoutDates = useCallback(
    async (checkinDate) => {
      if (!propertyId || !checkinDate) return {};
      return getCheckoutAvailability(propertyId, checkinDate);
    },
    [propertyId, getCheckoutAvailability]
  );

  const [searchTriggered, setSearchTriggered] = useState(false);

  const { queryResults: searchQueryResults } = useSearchData(propertyId, checkin, checkout, adults, searchTriggered);

  const { data: roomOffers, isLoading: isSearchLoading, isRefetching: isSearchRefetching, refetch: refetchSearch } = searchQueryResults;

  const isSearchLoadingOrRefetching = isSearchLoading || isSearchRefetching;

  useEffect(() => {
    if (!isSearchLoading) {
      setSearchTriggered(false);
    }
  }, [isSearchLoading]);

  const handleDateChange = ({ checkin: newCheckin, checkout: newCheckout }) => {
    onChange('checkin', newCheckin);
    onChange('checkout', newCheckout);
    onChange('error', null);
  };

  const handleAdultsChange = (value) => {
    onChange('adults', value);
  };

  const handleOfferSelect = (offer) => {
    onChange('selectedRoom', offer.roomId);
    onChange('selectedPlan', offer.planId != null ? { planId: offer.planId, totalPrice: offer.totalPrice } : null);
    onChange('totalPrice', offer.totalPrice);
    onSubmit();
  };

  const handleSearch = () => {
    if (!roomOffers) {
      setSearchTriggered(true);
    } else refetchSearch();
  };

  return (
    <Box>

      <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Minimalny pobyt:<br />
        - sezon wakacyjny: 7 nocy (sobota - sobota)<br />
        - długie weekendy: 3 noce<br />
        - pozostałe terminy: 2 noce<br />
      </Typography>
      <DatesSelector
        checkin={checkin}
        checkout={checkout}
        adults={adults}
        onDateChange={handleDateChange}
        onAdultsChange={handleAdultsChange}
        minDate={today}
        getAvailableCheckinDates={getAvailableCheckinDates}
        getAvailableCheckoutDates={getAvailableCheckoutDates}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <OrdinaryButton
          onClick={handleSearch}
          disabled={!checkin || !checkout || !adults}
          loading={isSearchLoading}
          text="Szukaj"
        />
      </Box>
      <Box sx={{ mt: 4 }}>
        {!isSearchLoadingOrRefetching && roomOffers && (
          roomOffers.length > 0
            ? <RoomOffers
                roomOffers={roomOffers}
                booking={booking}
                onSelectOffer={handleOfferSelect}
              />
            : <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Brak dostępnych ofert dla wybranych terminów i liczby osób.
              </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DatesAndAccommodationStep;
